import React, { useEffect, useMemo, useState } from 'react';
import { getDashboardActivity, getValetEfficiency } from '../services/api';
import BarChart from '../components/charts/BarChart';
import PieChart from '../components/charts/PieChart';
import LineChart from '../components/charts/LineChart';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import './Dashboard.css';

ChartJS.register(ArcElement, Tooltip, Legend);

/* CSS var → JS color (with fallback) */
const cssVar = (name, fb) =>
  (typeof window !== 'undefined'
    ? getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    : '') || fb;

export default function Home() {
  /* ---------------- state ---------------- */
  const [activity, setActivity] = useState([]);
  const [valetTop, setValetTop] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  /* brand colors from tokens */
  const BRAND   = cssVar('--brand-500', '#5B7CFA');
  const ACCENT  = cssVar('--accent-500', '#BA00BA');
  const NEUTRAL = '#E5E7EB';

  /* ---------------- fetch ---------------- */
  useEffect(() => {
    (async () => {
      try {
        const today = new Date();
        const start = new Date();
        start.setDate(today.getDate() - 6);

        const { buckets } = await getDashboardActivity({
          period: 'day',
          start_date: start.toISOString().slice(0, 10),
          end_date: today.toISOString().slice(0, 10),
        });
        setActivity(buckets);

        const { results } = await getValetEfficiency({ top_n: 10, period: '30_days' });
        setValetTop(results);
      } catch (err) {
        console.error(err);
        setError('Could not load dashboard data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ---------------- charts ---------------- */
  const barData = useMemo(() => {
    const labels =
      activity.length === 0
        ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
        : activity.map((b) =>
            new Date(b.start).toLocaleDateString(undefined, { weekday: 'short' })
          );
    return {
      labels,
      datasets: [
        {
          label: 'Check-Ins',
          data: activity.length ? activity.map((b) => b.ride_count) : Array(7).fill(0),
          backgroundColor: BRAND,
        },
        {
          label: 'Check-Outs',
          data: activity.length ? activity.map((b) => b.finished_count) : Array(7).fill(0),
          backgroundColor: ACCENT,
        },
      ],
    };
  }, [activity, BRAND, ACCENT]);

  // Placeholder slices (until endpoints exist)
  const pieData = useMemo(
    () => ({
      labels: ['Tips', 'Fees', 'Others'],
      datasets: [{ data: [30, 20, 35], backgroundColor: [ACCENT, BRAND, NEUTRAL] }],
    }),
    [ACCENT, BRAND]
  );
  const pieOptions = { plugins: { legend: { position: 'right', align: 'center' } }, maintainAspectRatio: false };

  const lineData = useMemo(
    () => ({
      labels: ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'],
      datasets: [
        { label: 'Average Review', data: [3.2, 3.5, 4.0, 4.2, 3.8, 4.4, 4.6], borderColor: BRAND, fill: false },
      ],
    }),
    [BRAND]
  );

  /* paging for valets */
  const PAGE_SIZE = 10;
  const [startIdx, setStartIdx] = useState(0);
  const visibleValets = valetTop.slice(startIdx, startIdx + PAGE_SIZE);

  if (loading) return <p className="pad">Loading dashboard…</p>;
  if (error) return <p className="pad error">{error}</p>;

  return (
    <div className="home page">
      {/* Row 1 */}
      <div className="row">
        <div className="card">
          <div className="card-head">
            <h3>Weekly Activity</h3>
          </div>
          <div className="chart-box">
            <BarChart chartData={barData} />
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>Revenue Statistics</h3>
          </div>
          <div className="chart-box center">
            <div className="pie-wrap">
              <PieChart pieData={pieData} pieOptions={pieOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Row 2 */}
      <div className="row">
        <div className="card">
          <div className="card-head">
            <h3>Top Valets (30 days)</h3>
            <div className="nav-arrows">
              <button
                className="btn btn-chip"
                onClick={() => setStartIdx((i) => Math.max(0, i - PAGE_SIZE))}
                disabled={startIdx === 0}
              >
                &lt;
              </button>
              <button
                className="btn btn-chip"
                onClick={() =>
                  setStartIdx((i) => (i + PAGE_SIZE < valetTop.length ? i + PAGE_SIZE : i))
                }
                disabled={startIdx + PAGE_SIZE >= valetTop.length}
              >
                &gt;
              </button>
            </div>
          </div>

          <div className="valet-grid">
            {visibleValets.map((v, i) => (
              <div className="valet-card" key={i}>
                <img
                  src="https://media.sproutsocial.com/uploads/2022/06/profile-picture.jpeg"
                  alt="Valet"
                />
                <p className="valet-name">{v.valet_name}</p>
                <span className="valet-sub">{v.rides_completed} rides</span>
              </div>
            ))}
            {visibleValets.length === 0 && <div className="empty">No data.</div>}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>Average Review</h3>
          </div>
          <div className="chart-box">
            <LineChart lineData={lineData} />
          </div>
        </div>
      </div>
    </div>
  );
}
